import { PreviousShiftEvent } from "@type/workMonitor.types"
import { Modal } from "antd"
import { FilePdfOutlined } from "@ant-design/icons"
import { AntdButton } from "components"
import { reportService } from "services/"
import { useContext, useMemo, useState } from "react"
import QrTable from "./QRTable"
import CheckCallsTable from "./CheckCallsTable"
import IncidentsTable from "./IncidentsSection"
import InventoryTable from "./InventoryTable"
import Header from "../Header"
import Footer from "../Footer"
import Highlights from "../Highlights"
import { ShiftStatus } from "config/constants"
import ShiftEventsRetainContext, { ShiftEventsContextProps } from "../../ShiftEventsContext"

interface Props {
	open: boolean
	onClose: () => void
}

const ShiftActivityModal = ({ open, onClose }: Props) => {
	const { shift } = useContext(ShiftEventsRetainContext) as ShiftEventsContextProps
	if (!shift || !open) return null
	const [isGeneratingActivityReport, setIsGeneratingActivityReport] = useState(false)
	const { bookOnEvent, bookOffEvent, qrEvents, checkEvents, inventoryEvents } = useMemo(() => {
		const { shiftEvents = [] } = shift

		return shiftEvents.reduce(
			(acc, event) => {
				switch (event.status) {
					case ShiftStatus.BOOKON:
						acc.bookOnEvent.push(event)
						break
					case ShiftStatus.BOOKOFF:
						acc.bookOffEvent.push(event)
						break
					case ShiftStatus.QR:
						acc.qrEvents.push(event)
						break
					case ShiftStatus.CHECK:
						acc.checkEvents.push(event)
						break
					case ShiftStatus.INVENTORY:
						acc.inventoryEvents.push(event)
						break
				}
				return acc
			},
			{
				bookOnEvent: [] as PreviousShiftEvent[],
				bookOffEvent: [] as PreviousShiftEvent[],
				qrEvents: [] as PreviousShiftEvent[],
				checkEvents: [] as PreviousShiftEvent[],
				inventoryEvents: [] as PreviousShiftEvent[],
			},
		)
	}, [shift])

	const headerData = useMemo(() => {
		return {
			title: "SHIFT ACTIVITY REPORT",
			date: shift.date,
			customer: shift.customer,
			address: shift.address,
			location: shift.location,
			employee: shift.employee,
			post: shift.post,
			startTime: shift.startTime,
			finishTime: shift.finishTime,
			employeeQualification: shift.employeeQualification,
			bookOnEvent,
			bookOffEvent,
		}
	}, [shift, bookOnEvent, bookOffEvent])

	const highlightsData = useMemo(() => {
		const lateCheckThreshold = parseInt(shift.siteCallsWindow.checkCallWindow?.after)

		return {
			geoFenceBreachCount: shift.geoFenceBreachCount,
			incidentEvents: shift.incidents,
			checkEvents: checkEvents,
			qrEvents: qrEvents,
			lateCheckThreshold,
		}
	}, [shift, checkEvents, qrEvents])

	return (
		<Modal
			title={
				<div className="flex w-full items-center justify-between">
					<span className="text-lg font-semibold">Shift Activity Report</span>
					<div className="mr-5">
						<AntdButton
							className="flex items-center justify-center "
							icon={<FilePdfOutlined />}
							loading={isGeneratingActivityReport}
							onClick={async () => {
								try {
									setIsGeneratingActivityReport(true)
									if (!shift?._id) return
									const res = await reportService.generateShiftActivityReport(shift)
									const file = new Blob([res.data])
									const link = document.createElement("a")
									link.href = URL.createObjectURL(file)
									link.download = "shift-report.pdf"
									link.click()
									URL.revokeObjectURL(link.href)
								} catch (error) {
									console.error(error)
								} finally {
									setIsGeneratingActivityReport(false)
								}
							}}
						>
							Download Report
						</AntdButton>
					</div>
				</div>
			}
			open={open}
			width={1200}
			onCancel={onClose}
			footer={null}
		>
			{shift ? (
				<div className="font-inter w-full  space-y-10 px-7 text-[#101828]">
					{/* HEADER */}
					<Header data={headerData} />

					{/* CHECK CALL */}
					<CheckCallsTable
						checkCallWindow={shift.siteCallsWindow?.checkCallWindow.after}
						checkEvents={checkEvents}
					/>

					{/* QR PATROLS*/}
					<QrTable acceptanceCriteria={shift.acceptanceCriteria} qrEvents={qrEvents} />

					{/* INVENTORY */}
					<InventoryTable inventoryEvents={inventoryEvents} />

					{/* INCIDENT */}
					<IncidentsTable incidents={shift.incidents} />

					{/* HIGHLIGHTS */}
					<Highlights data={highlightsData} />

					{/* FOOTER*/}
					<Footer />
				</div>
			) : (
				<p>No shift information available.</p>
			)}
		</Modal>
	)
}

export default ShiftActivityModal
